import React, { useContext } from 'react';
import * as R from 'ramda';
import PayInvoice from './PayInvoice';
import CCView from './CCView';
import Account from './Account';
import ChangePwd from './ChangePwd';
import InvoiceList from './InvoiceList';
import { Switch, Route, useHistory } from 'react-router-dom';
import '../css/reset.scss';
import styles from './css/PaymentApp.scss';
import { capitalize } from '@cd/utils';
import { CenterProgress } from '../component/CenterProgress';
import { Elements } from '@stripe/react-stripe-js';
import TabsCD from '../component/TabsCD';
import PayGatewayInfo from './PayGatewayInfo';
import TermsAndConditions from './TermsAndConditions';
import Signout from './Signout';
import Services from './Services';
import useSideTabs from './fetch/useSideTabs';
import { store } from './store';
import useStripeKeyPub from './fetch/useStripeKeyPub';
import { DynamicDefaultCmp, DynamicImport } from '../DynamicImport';
import ApplyPaymentToInvoices from './root/ApplyPaymentToInvoices';
import { usePathMatch, useRedirect } from '../core/routerHooks';
import Legal from './Legal';
import OrganizationInfo from './OrganizationInfo';
import SubscribeService from './SubscribeService';
import PaymentMethods from './PaymentMethods';
import PaymentMethodStatus from './PaymentMethodStatus';
import Setup2FA from './Setup2FA';

const dropLastScriptLetter = R.compose(R.join(''), R.init, R.split(''));

const PaymentApp = ({ APP_ID, childRoute, childRouteSearch }) => {
	const history = useHistory();
	const { dispatch } = useContext(store);

	const params = usePathMatch(childRoute);
	const routeName = params?.routeName;
	const navRouteName = params?.navRouteName;
	useRedirect({
		childRoute,
		from: '/account',
		to: '/account/personal/info'
	});

	const {
		isLoading: isStripeLoading,
		isError: isStripeError,
		error: stripeError,
		data: stripeData
	} = useStripeKeyPub(dispatch);

	const {
		isLoading: isTabsLoading,
		isError: isTabsError,
		error: tabsError,
		data: sideNavTabs
	} = useSideTabs(dispatch, { routeName });

	// Only Stripe gates the whole widget. Side tabs load inside the nav panel
	// so a route change never unmounts the page being shown.
	if (isStripeLoading) return <CenterProgress />;

	if (isStripeError) return <div>{stripeError && stripeError.message}</div>;

	function onTabChange(route) {
		history.push(route);
	}

	return (
		<div className={styles.main}>
			<div className={styles.invoiceNavPanel}>
				{isTabsLoading ? (
					<CenterProgress />
				) : isTabsError ? (
					<div>{tabsError && tabsError.message}</div>
				) : (
					navRouteName &&
					sideNavTabs?.[routeName] && (
						<TabsCD
							color="secondary"
							tabs={sideNavTabs[routeName]}
							onTabChange={onTabChange}
							dark={true}
							initialValue={childRoute}
							orientation="vertical"
						/>
					)
				)}
			</div>
			<div className={styles.invoicePanel}>
				<Switch>
					<Route exact path="/account/invoices/list">
						<InvoiceList />
					</Route>
					<Route exact path="/account">
						<CenterProgress />
					</Route>
					<Route exact path="/account/invoices/gateway">
						<PayGatewayInfo />
					</Route>
					<Route exact path="/account/invoices/terms">
						<TermsAndConditions />
					</Route>
					<Route
						path="/account/pay/:id"
						render={props => (
							<Elements stripe={stripeData}>
								<PayInvoice {...props} />
							</Elements>
						)}
					/>
					<Route path="/account/personal/pwd">
						<ChangePwd />
					</Route>
					<Route path="/account/personal/2fa">
						<Setup2FA />
					</Route>
					<Route path="/account/personal/info">
						<Account />
					</Route>
					<Route path="/account/personal/organization">
						<OrganizationInfo />
					</Route>
					<Route
						path="/account/personal/paymentmethods"
						render={props => <PaymentMethods {...props} />}
					/>
					<Route
						path="/account/personal/payment/method/status"
						render={props => (
							<Elements stripe={stripeData}>
								<PaymentMethodStatus
									{...props}
									childRouteSearch={childRouteSearch}
								/>
							</Elements>
						)}
					/>
					<Route
						path="/account/services/current"
						render={props => <Services {...props} />}
					/>
					<Route path="/account/services/subscribe">
						<SubscribeService />
					</Route>
					<Route path="/account/card/view">
						<CCView />
					</Route>
					<Route
						path="/account/testforms/:id"
						render={props => <ApplyPaymentToInvoices {...props} />}
					/>

					<Route
						path="/account/clientmgr/:script/:id?/:param1?/:param2?"
						exact={false}
						render={props => {
							const {
								match: {
									params: { script, id }
								}
							} = props;

							console.log('props', props);

							const finalScriptName = R.compose(
								R.when(() => id, dropLastScriptLetter),
								capitalize
							)(script);

							console.log('finalScriptName', finalScriptName);

							return (
								<DynamicImport
									key={finalScriptName}
									load={() =>
										import(`./root/${finalScriptName}.js`)
									}
									{...props}>
									{DynamicDefaultCmp}
								</DynamicImport>
							);
						}}
					/>
					<Route
						path="/account/legal/list/:contractID?"
						render={props => <Legal {...props} />}
					/>
					<Route exact path="/account/signout">
						<Signout />
					</Route>
				</Switch>
			</div>
			<div className={styles.spacer}>&nbsp;</div>
		</div>
	);
};

export default PaymentApp;
